import { api, PUBLIC_ADMIN_AUTH_URL } from "@/app/api";
import type { IssuedTokens } from "@/lib/auth";
import type { AdminProfile } from "@/types/api";

/** `POST /auth/forgot-password` and `/auth/resend-code` — a reset code was emailed. */
export interface OtpChallenge {
  /** Masked, e.g. `j***@company.com`. Safe to display. */
  email: string;
  expires_in: number;
  resend_available_in: number;
  resends_remaining: number;
  /** Development only — the code itself, when the API echoes it. */
  dev_code?: string;
}

/** `POST /auth/login` — signed in. */
export interface AdminSignIn extends IssuedTokens {
  token_type: "Bearer";
  session_id: string;
  admin: { id: string; email: string; name: string; is_owner: boolean };
}

/**
 * The signed-in admin, and the way in.
 *
 * `GET /me` is the first call every session makes, and it answers three
 * questions at once: who is this, what may they do, and does their token still
 * work. The panel asks the server rather than decoding the token, because the
 * API resolves permissions from the database on every request precisely so
 * that a revoked role takes effect immediately — reading them from a JWT claim
 * here would reintroduce exactly the staleness that design avoids.
 *
 * Sign-in is one step on the public audience: email and password return the
 * tokens. The only emailed code left is the forgot-password one. Refresh is not
 * here — it runs inside the base query, where the 401 that calls for it is
 * seen.
 *
 * Endpoints are *injected* into the shared api object rather than defining a
 * new one. Every feature does this, which is what lets a mutation in one
 * feature invalidate a query in another through the shared tag registry.
 */
export const authApi = api.injectEndpoints({
  endpoints: (builder) => ({
    getCurrentAdmin: builder.query<AdminProfile, void>({
      query: () => "/me",
      // `Profile` as well as `Admin`: saving your own name on the profile
      // screen has to move the name in the top bar, and the top bar reads this
      // query rather than that one.
      providesTags: ["Admin", "Profile"],
    }),

    login: builder.mutation<AdminSignIn, { email: string; password: string }>({
      query: (body) => ({ url: `${PUBLIC_ADMIN_AUTH_URL}/login`, method: "POST", body }),
    }),

    /** Emails a reset code. Answers identically whether or not the address is an admin. */
    forgotPassword: builder.mutation<OtpChallenge, { email: string }>({
      query: (body) => ({ url: `${PUBLIC_ADMIN_AUTH_URL}/forgot-password`, method: "POST", body }),
    }),

    /**
     * Another reset code. `password_reset` is the only purpose the API still
     * accepts. An unknown address gets the same 200 and nothing is sent, so a
     * success here is not proof an email went out.
     */
    resendResetCode: builder.mutation<OtpChallenge, { email: string }>({
      query: ({ email }) => ({
        url: `${PUBLIC_ADMIN_AUTH_URL}/resend-code`,
        method: "POST",
        body: { email, purpose: "password_reset" },
      }),
    }),

    /**
     * `204`. Signs the admin out everywhere and does *not* sign them in — the
     * next stop is the sign-in form.
     */
    resetPassword: builder.mutation<void, { email: string; code: string; new_password: string }>({
      query: (body) => ({ url: `${PUBLIC_ADMIN_AUTH_URL}/reset-password`, method: "POST", body }),
    }),
  }),
});

export const {
  useGetCurrentAdminQuery,
  useLazyGetCurrentAdminQuery,
  useLoginMutation,
  useForgotPasswordMutation,
  useResendResetCodeMutation,
  useResetPasswordMutation,
} = authApi;
